import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageCircleOff, Pencil, X } from 'lucide-react';
import { api, type ChatBanDto } from '@/lib/api';

const DURATION_PRESETS = [12, 24, 48, 96, 168];
const MIN_STARTS_AT_ISO = '2026-08-01T00:00:00.000Z';

function minStartsAtLocal(): string {
  const d = new Date(MIN_STARTS_AT_ISO);
  const tzOffset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - tzOffset * 60_000);
  return local.toISOString().slice(0, 16);
}

function pad(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

function nowLocalDatetimeValue(): string {
  const d = new Date();
  const tzOffset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - tzOffset * 60_000);
  return local.toISOString().slice(0, 16);
}

function localToIso(local: string): string {
  return new Date(local).toISOString();
}

function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const tzOffset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - tzOffset * 60_000);
  return local.toISOString().slice(0, 16);
}

function formatCountdown(ms: number): string {
  if (ms <= 0) return 'ended';
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (days > 0) return `${days}d ${pad(hours)}:${pad(mins)}:${pad(secs)}`;
  return `${pad(hours)}:${pad(mins)}:${pad(secs)}`;
}

export default function ChatBanCard({ token }: { token: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['chat-ban', token],
    queryFn: () => api.getChatBans(token),
    enabled: !!token,
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');

  const active = q.data?.active ?? null;

  const endMut = useMutation({
    mutationFn: (id: number) => api.endChatBan(token, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['chat-ban', token] }),
  });

  return (
    <section className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3">
      {active ? (
        <ActiveBanBody
          ban={active}
          onEdit={() => {
            setMode('edit');
            setDialogOpen(true);
          }}
          onEnd={() => {
            if (confirm('End the chat ban now?')) endMut.mutate(active.id);
          }}
          ending={endMut.isPending}
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            setMode('create');
            setDialogOpen(true);
          }}
          className="flex w-full items-center gap-3 rounded-md py-1 text-left"
        >
          <MessageCircleOff size={18} className="text-rose-300" />
          <div className="flex-1">
            <div className="text-sm font-medium text-slate-100">
              Report a chat ban
            </div>
            <div className="text-[11px] text-slate-400">
              Let the alliance know you got muted in-game.
            </div>
          </div>
          <span className="text-xs text-slate-500">Tap</span>
        </button>
      )}

      {dialogOpen && (
        <ChatBanDialog
          token={token}
          mode={mode}
          existing={mode === 'edit' ? active : null}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </section>
  );
}

function ActiveBanBody({
  ban,
  onEdit,
  onEnd,
  ending,
}: {
  ban: ChatBanDto;
  onEdit: () => void;
  onEnd: () => void;
  ending: boolean;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const endsMs = Date.parse(ban.endsAtUtc);
  const delta = endsMs - now;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <MessageCircleOff size={16} className="text-rose-300" />
        <div className="text-sm font-medium text-slate-100">
          Chat ban · {ban.durationHours}h
        </div>
      </div>
      <div className="text-xs text-slate-400">
        Ends in{' '}
        <span className="font-semibold tabular-nums text-slate-200">
          {formatCountdown(delta)}
        </span>
      </div>
      {ban.notes && (
        <div className="text-[11px] italic text-slate-500">"{ban.notes}"</div>
      )}
      <div className="flex gap-2 pt-1">
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-950 px-2.5 py-1 text-xs text-slate-200 hover:border-slate-500"
        >
          <Pencil size={12} /> Edit
        </button>
        <button
          type="button"
          onClick={onEnd}
          disabled={ending}
          className="inline-flex items-center gap-1 rounded border border-rose-700/60 bg-rose-950/60 px-2.5 py-1 text-xs text-rose-100 hover:border-rose-500 disabled:opacity-50"
        >
          <X size={12} /> End now
        </button>
      </div>
    </div>
  );
}

function ChatBanDialog({
  token,
  mode,
  existing,
  onClose,
}: {
  token: string;
  mode: 'create' | 'edit';
  existing: ChatBanDto | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [startsLocal, setStartsLocal] = useState<string>(() => {
    const initial = existing
      ? isoToLocalInput(existing.startsAtUtc)
      : nowLocalDatetimeValue();
    const min = minStartsAtLocal();
    return initial < min ? min : initial;
  });
  const [durationHours, setDurationHours] = useState<number>(
    existing?.durationHours ?? 24,
  );
  const [notes, setNotes] = useState<string>(existing?.notes ?? '');
  const [error, setError] = useState<string | null>(null);

  const isFuture = useMemo(
    () => Date.parse(localToIso(startsLocal)) > Date.now() + 60_000,
    [startsLocal],
  );

  const createMut = useMutation({
    mutationFn: (body: {
      startsAtUtc: string;
      durationHours: number;
      notes?: string;
    }) => api.createChatBan(token, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-ban', token] });
      onClose();
    },
    onError: (e: unknown) => setError((e as Error).message),
  });
  const updateMut = useMutation({
    mutationFn: (body: {
      startsAtUtc?: string;
      durationHours?: number;
      notes?: string | null;
    }) => api.updateChatBan(token, existing!.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-ban', token] });
      onClose();
    },
    onError: (e: unknown) => setError((e as Error).message),
  });

  const submit = () => {
    setError(null);
    const iso = localToIso(startsLocal);
    if (mode === 'create') {
      createMut.mutate({
        startsAtUtc: iso,
        durationHours,
        notes: notes.trim() ? notes.trim() : undefined,
      });
    } else {
      updateMut.mutate({
        startsAtUtc: iso,
        durationHours,
        notes: notes.trim() ? notes.trim() : null,
      });
    }
  };

  const busy = createMut.isPending || updateMut.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/70 p-4 sm:items-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-slate-800 bg-slate-950 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">
            {mode === 'create' ? 'Report chat ban' : 'Edit chat ban'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:text-slate-100"
          >
            <X size={16} />
          </button>
        </div>

        <label className="mb-3 block text-xs">
          <span className="mb-1 block text-slate-400">Started at</span>
          <input
            type="datetime-local"
            value={startsLocal}
            min={minStartsAtLocal()}
            onChange={(e) => setStartsLocal(e.target.value)}
            style={{
              color: '#e2e8f0',
              backgroundColor: '#020617',
              fontSize: '16px',
            }}
            className="w-full rounded border border-slate-700 px-2 py-2"
          />
          {isFuture && (
            <span className="mt-1 block text-[10px] text-amber-300">
              Start is in the future — the ban will only be "active" from that time.
            </span>
          )}
        </label>

        <div className="mb-3">
          <div className="mb-1 text-xs text-slate-400">Duration</div>
          <div className="flex flex-wrap gap-1.5">
            {DURATION_PRESETS.map((h) => (
              <button
                key={h}
                type="button"
                onClick={() => setDurationHours(h)}
                className={
                  'rounded border px-2.5 py-1 text-xs ' +
                  (durationHours === h
                    ? 'border-indigo-500 bg-indigo-600/30 text-indigo-100'
                    : 'border-slate-700 bg-slate-950 text-slate-400')
                }
              >
                {h}h
              </button>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
            <button
              type="button"
              onClick={() =>
                setDurationHours((d) => Math.max(12, d - 12))
              }
              className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-200"
            >
              −12h
            </button>
            <span className="tabular-nums text-slate-200">
              {durationHours}h
            </span>
            <button
              type="button"
              onClick={() =>
                setDurationHours((d) => Math.min(336, d + 12))
              }
              className="rounded border border-slate-700 bg-slate-950 px-2 py-1 text-slate-200"
            >
              +12h
            </button>
          </div>
        </div>

        <label className="mb-3 block text-xs">
          <span className="mb-1 block text-slate-400">Notes (optional)</span>
          <input
            type="text"
            maxLength={120}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. said too much in world chat"
            style={{
              color: '#e2e8f0',
              backgroundColor: '#020617',
              fontSize: '16px',
            }}
            className="w-full rounded border border-slate-700 px-2 py-2"
          />
        </label>

        {error && (
          <div className="mb-2 text-xs text-rose-300">{error}</div>
        )}

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded border border-slate-700 bg-slate-950 py-2 text-sm text-slate-300"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="flex-1 rounded bg-indigo-600 py-2 text-sm font-medium hover:bg-indigo-500 disabled:opacity-50"
          >
            {busy ? 'Saving…' : mode === 'create' ? 'Submit' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
